import { useFlag } from '@calendly/feature-check-react';
import { useBooleanState } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CloseIcon, QuestionCircleIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { BodyText, HeadlineText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useCallback, useEffect, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMyCalendlyOwners } from 'actions/workflows';
import { useGetPolicyQuery, useGetUserQuery, useUpdateUserActivationDataMutation } from 'common';
import { useUpgradeSubscription } from 'components/billing/use_upgrade_subscription';
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell';
import { isRestrictedFeatureSelector } from 'features/billing';
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events';
import { ownerCollectionSelector, ownerListLoadedSelector } from 'selectors/workflows';
import { NO_SHOW_TEMPLATE_ID } from 'services/workflows/templates';
import { appNewWorkflowFromTemplateUrl } from 'services/workflows/url';
const containerCss = "ckdnuzv";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const headerContainerCss = "h6y50ci";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, props.className)} />;
});
const buttonContainerCss = "b8tn3ie";
const ButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonContainerCss, props.className)} />;
});
const learnMoreLinkCss = "l1eve3is";
const LearnMoreLink = forwardRef((props, ref) => <Link ref={ref} {...props} className={classNames(learnMoreLinkCss, props.className)} />);
const WorkflowLink = ({
  onClick,
  children,
  isWorkflowsAppShellRebrand
}) => <Link href={appNewWorkflowFromTemplateUrl({
  type: 'user',
  id: 'me'
}, NO_SHOW_TEMPLATE_ID, undefined, isWorkflowsAppShellRebrand)}>
    <Button onClick={onClick} responsive={false}>
      {children}
    </Button>
  </Link>;
const UpgradeLink = ({
  onClick,
  children
}) => {
  const upgradeSubscription = useUpgradeSubscription({
    featureKey: 'workflows',
    paywall: 'workflows_workflows_banner',
    type: 'inline'
  });
  return <Button onClick={() => {
    onClick();
    upgradeSubscription();
  }} responsive={false}>
      {children}
    </Button>;
};
const learnMoreUrl = 'https://calendly.com/help/workflows-overview';
const storageKey = 'no_show_banner_seen_count';
const useSeenCounter = user => {
  const [updateActivationData] = useUpdateUserActivationDataMutation();
  const value = user?.activation_data[storageKey] || 0;
  const increase = () => {
    if (!user) return;
    updateActivationData({
      [storageKey]: value + 1
    });
  };
  return {
    value,
    increase
  };
};
const useNoShowWorkflows = () => {
  const workflowsExtractionEnabled = useFlag('raven_workflows_extraction');
  const dispatch = useDispatch();
  const owners = useSelector(ownerCollectionSelector);
  const ownersLoaded = useSelector(ownerListLoadedSelector);
  const noShowWorkflowExists = owners.some(owner => owner.workflows.collection.some(workflow => workflow.workflow_rule.event_name === 'invitee_no_show'));
  useEffect(() => {
    dispatch(fetchMyCalendlyOwners({
      page: 1,
      workflowsExtractionEnabled
    }));
  }, [dispatch, workflowsExtractionEnabled]);
  return {
    exist: noShowWorkflowExists,
    isLoading: !ownersLoaded
  };
};
const useAnalytics = ({
  enabled,
  user,
  canUseWorkflows
}) => {
  const track = useTrack();
  const sendEvent = useCallback((name, properties) => {
    track({
      name,
      properties: {
        cta_text: canUseWorkflows ? 'create_workflow' : 'upgrade_plan',
        ...properties
      }
    });
  }, [track, canUseWorkflows]);
  const onCreateWorkflow = () => {
    sendEvent(TRACKING_EVENT_NAMES.CTA_CLICKED, {
      cta_key: 'meetings_page_right_rail',
      cta_destination: 'no_show_workflows_setup_flow',
      action: 'create_workflow'
    });
  };
  const onUpgradePlan = () => {
    sendEvent(TRACKING_EVENT_NAMES.CTA_CLICKED, {
      cta_key: 'meetings_page_right_rail',
      cta_destination: 'paywall',
      action: 'open_paywall'
    });
  };
  const onClose = () => {
    sendEvent(TRACKING_EVENT_NAMES.CTA_CLICKED, {
      cta_key: 'meetings_page_right_rail',
      action: 'dismissed'
    });
  };
  const onLearnMore = () => {
    sendEvent(TRACKING_EVENT_NAMES.CTA_CLICKED, {
      cta_key: 'meetings_page_right_rail',
      action: 'open_help_center'
    });
  };
  useEffect(() => {
    if (!enabled || !user.uuid) return;
    sendEvent(TRACKING_EVENT_NAMES.CTA_SHOWN, {
      cta_key: 'meetings_page_right_rail'
    });
  }, [sendEvent, enabled, user?.uuid]);
  return {
    onCreateWorkflow,
    onUpgradePlan,
    onClose,
    onLearnMore
  };
};
const useShowBanner = user => {
  const [open, _, close] = useBooleanState(true);
  const seenCounter = useSeenCounter(user);
  const organizationCanUseWorkflows = !useSelector(state => isRestrictedFeatureSelector(state, {
    featureKey: 'workflows'
  }));
  const policyQuery = useGetPolicyQuery();
  const noShowWorkflows = useNoShowWorkflows();
  const organizationDisabledWorkflows = !policyQuery.data.canUseWorkflows && organizationCanUseWorkflows;
  const isLoading = !user || policyQuery.isFetching || noShowWorkflows.isLoading;
  if (!open || isLoading || noShowWorkflows.exist || seenCounter.value >= 2 || organizationDisabledWorkflows) return {};
  return {
    show: true,
    canUseWorkflows: organizationCanUseWorkflows,
    onClose: () => {
      seenCounter.increase();
      close();
    }
  };
};
export const NoShowBanner = () => {
  const {
    data: user
  } = useGetUserQuery();
  const {
    show,
    canUseWorkflows,
    onClose
  } = useShowBanner(user);
  const analytics = useAnalytics({
    user,
    canUseWorkflows,
    enabled: show
  });
  const isWorkflowsAppShellRebrand = useProductRefreshAppShell();
  if (!show) return null;
  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
    analytics.onClose();
  };
  return <Container data-testid="no-show-banner">
      <HeaderContainer>
        <HeadlineText level={1} size="small">
          {I18n.t('publisher.meetings.invitees.no_show_banner.title')}
        </HeadlineText>

        <IconButton onClick={() => handleClose()} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </HeaderContainer>

      <BodyText>
        {canUseWorkflows ? I18n.t(isWorkflowsAppShellRebrand ? 'publisher.workflows_app_shell_rebrand.meetings.no_show_banner.description' : 'publisher.meetings.invitees.no_show_banner.description') : I18n.t(isWorkflowsAppShellRebrand ? 'publisher.workflows_app_shell_rebrand.meetings.no_show_banner.description_without_access' : 'publisher.meetings.invitees.no_show_banner.description_without_access')}
      </BodyText>

      <ButtonContainer>
        <LearnMoreLink target="_blank" rel="opener" onClick={() => analytics.onLearnMore()} href={learnMoreUrl}>
          <QuestionCircleIcon />
          {I18n.t('publisher.meetings.invitees.no_show_banner.learn_more')}
        </LearnMoreLink>

        {canUseWorkflows ? <WorkflowLink onClick={() => analytics.onCreateWorkflow()} isWorkflowsAppShellRebrand={isWorkflowsAppShellRebrand}>
                {I18n.t('publisher.meetings.invitees.no_show_banner.action')}
              </WorkflowLink> : <UpgradeLink onClick={() => analytics.onUpgradePlan()}>
                {I18n.t('publisher.meetings.invitees.no_show_banner.action_without_access')}
              </UpgradeLink>}
      </ButtonContainer>
    </Container>;
};

require("./no_show_banner.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./no_show_banner.js");